import React, { useState, useEffect } from 'react';

const KeyboardShortcuts = ({ isVisible, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(isVisible);
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '?' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const shortcuts = [
    { key: 'Ctrl/Cmd + A', description: 'Select all leads' },
    { key: 'Ctrl/Cmd + E', description: 'Export selected leads' },
    { key: 'Escape', description: 'Clear selection' },
    { key: 'Ctrl/Cmd + ?', description: 'Show/hide keyboard shortcuts' },
    { key: 'Tab', description: 'Navigate between elements' },
    { key: 'Enter/Space', description: 'Activate buttons and links' },
    { key: 'Arrow Keys', description: 'Navigate table rows' },
    { key: 'Ctrl/Cmd + F', description: 'Focus search field' }
  ];

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Keyboard Shortcuts</h2>
          <button 
            style={styles.closeButton}
            onClick={() => {
              setIsOpen(false);
              onClose?.();
            }}
            aria-label="Close keyboard shortcuts"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div style={styles.content}>
          <div style={styles.shortcutsGrid}>
            {shortcuts.map((shortcut, index) => (
              <div key={index} style={styles.shortcutItem}>
                <kbd style={styles.key}>{shortcut.key}</kbd>
                <span style={styles.description}>{shortcut.description}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Press <kbd style={styles.inlineKey}>Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.2s ease forwards'
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'hidden',
    backdropFilter: 'blur(20px)',
    animation: 'slideIn 0.3s ease forwards'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    margin: 0
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
      transform: 'translateY(-1px)'
    }
  },
  content: {
    flex: 1,
    overflow: 'auto'
  },
  shortcutsGrid: {
    display: 'grid',
    gap: '16px'
  },
  shortcutItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(255, 255, 255, 0.2)'
    }
  },
  key: {
    display: 'inline-block',
    padding: '4px 8px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ffffff',
    fontFamily: 'monospace',
    minWidth: '80px',
    textAlign: 'center'
  },
  inlineKey: {
    display: 'inline-block',
    padding: '2px 6px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#ffffff',
    fontFamily: 'monospace'
  },
  description: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500'
  },
  footer: {
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.7)',
    margin: 0
  }
};

export default KeyboardShortcuts; 